const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const flattenValue = (value) => {
	if (value === null || typeof value === "undefined") return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const escapeCsvValue = (value) => {
	const stringValue = String(flattenValue(value));
	return /[",\n\r]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
};

const escapeXml = (value) =>
	String(flattenValue(value))
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");

const getColumnLetter = (index) => {
	let column = "";
	let current = index + 1;

	while (current > 0) {
		const remainder = (current - 1) % 26;
		column = String.fromCharCode(65 + remainder) + column;
		current = Math.floor((current - 1) / 26);
	}

	return column;
};

const createCrcTable = () => {
	const table = [];
	for (let index = 0; index < 256; index++) {
		let value = index;
		for (let bit = 0; bit < 8; bit++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
		table[index] = value >>> 0;
	}
	return table;
};

const CRC_TABLE = createCrcTable();
const uint16 = (value) => [value & 0xff, (value >>> 8) & 0xff];
const uint32 = (value) => [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
const stringBytes = (value) => Array.from(new TextEncoder().encode(value));

const crc32 = (bytes) => {
	let crc = 0xffffffff;
	for (let index = 0; index < bytes.length; index++) crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
	return (crc ^ 0xffffffff) >>> 0;
};

const createZip = (files) => {
	const localParts = [];
	const centralParts = [];
	let offset = 0;

	files.forEach((file) => {
		const nameBytes = stringBytes(file.name);
		const dataBytes = stringBytes(file.content);
		const crc = crc32(dataBytes);
		const localHeader = [
			...uint32(0x04034b50), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0),
			...uint32(crc), ...uint32(dataBytes.length), ...uint32(dataBytes.length), ...uint16(nameBytes.length), ...uint16(0), ...nameBytes,
		];

		localParts.push(...localHeader, ...dataBytes);
		centralParts.push(
			...uint32(0x02014b50), ...uint16(20), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0),
			...uint32(crc), ...uint32(dataBytes.length), ...uint32(dataBytes.length), ...uint16(nameBytes.length), ...uint16(0), ...uint16(0),
			...uint16(0), ...uint16(0), ...uint32(0), ...uint32(offset), ...nameBytes,
		);
		offset += localHeader.length + dataBytes.length;
	});

	const endParts = [
		...uint32(0x06054b50), ...uint16(0), ...uint16(0), ...uint16(files.length), ...uint16(files.length),
		...uint32(centralParts.length), ...uint32(localParts.length), ...uint16(0),
	];
	const output = new Uint8Array(localParts.length + centralParts.length + endParts.length);
	output.set(localParts, 0);
	output.set(centralParts, localParts.length);
	output.set(endParts, localParts.length + centralParts.length);

	return new Blob([output], { type: XLSX_MIME_TYPE });
};

const getXlsxBlob = (rows, columns) => {
	const values = [
		columns.map((column) => column.label),
		...rows.map((row) => columns.map((column) => flattenValue(row[column.value]))),
	];
	const sheetData = values.map((row, rowIndex) => {
		const cells = row.map((value, columnIndex) => {
			const cellRef = `${getColumnLetter(columnIndex)}${rowIndex + 1}`;
			return `<c r="${cellRef}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
		}).join("");
		return `<row r="${rowIndex + 1}">${cells}</row>`;
	}).join("");

	return createZip([
		{ name: "[Content_Types].xml", content: '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>' },
		{ name: "_rels/.rels", content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>' },
		{ name: "xl/workbook.xml", content: '<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Achievements" sheetId="1" r:id="rId1"/></sheets></workbook>' },
		{ name: "xl/_rels/workbook.xml.rels", content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>' },
		{ name: "xl/styles.xml", content: '<?xml version="1.0" encoding="UTF-8"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs></styleSheet>' },
		{ name: "xl/worksheets/sheet1.xml", content: `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetData}</sheetData></worksheet>` },
	]);
};

export const downloadTableExport = ({ rows, columns, format, filename }) => {
	const selectedRows = rows.map((row) => columns.reduce((result, column) => {
		result[column.value] = row[column.value];
		return result;
	}, {}));
	let file;

	if (format === "csv") {
		const csvRows = rows.map((row) => columns.map((column) => escapeCsvValue(row[column.value])).join(","));
		const csv = `\uFEFF${[columns.map((column) => escapeCsvValue(column.label)).join(","), ...csvRows].join("\n")}`;
		file = new Blob([csv], { type: "text/csv;charset=utf-8" });
	} else if (format === "xlsx") {
		file = getXlsxBlob(rows, columns);
	} else {
		file = new Blob([JSON.stringify(selectedRows, null, 2)], { type: "application/json;charset=utf-8" });
	}

	const link = document.createElement("a");
	const objectUrl = URL.createObjectURL(file);
	link.href = objectUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(objectUrl);
};
